import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select/creatable';
import Swal from 'sweetalert2';
import ToggleSwitch from 'react-switch';
import { useHistory } from 'react-router-dom';

import { blackberry, coolGrey, grape, greyStroke, squidInk, white } from 'lib/css/colors';
import { GLOBAL_VENDORS_CREATE } from 'lib/networking/endpoints';
import { P0SyncAttributeOptions } from 'lib/enums/P0Attributes';
import { selectStyles } from 'lib/css/selectStyles';
import usePost from 'lib/hooks/usePost';

const TEXT_FIELDS = ['name', 'cnd_id'];
const MULTI_SELECT_FIELDS = ['excluded_p0_sync_attributes'];

const vendorRows = {
  name: 'Distributor Name',
  cnd_id: 'Cut and Dry ID',
  excluded_p0_sync_attributes: 'Attributes excluded from P0 sync',
  p0_sync_enabled: 'P0 sync is enabled',
  is_proprietary_images: 'Is proprietary images',
  cnd_image_sync_enabled: 'Cut and Dry image sync enabled',
  is_dp_partner: 'Is a distributor portal partner',
  is_test_vendor: 'Is Test Distributor',
};

function VendorCreateModal({ onHide }) {
  const [selectedOptions, setSelectedOptions] = useState();
  const [newVendor, setNewVendor] = useState({});
  const history = useHistory();

  const { postData: createVendor, loading } = usePost(
    GLOBAL_VENDORS_CREATE,
    response => {
      const vendor = response.data.vendor;
      Swal.fire({ title: 'Distributor created successfully.', icon: 'success' }).then(() => {
        onHide();
        history.push('/enrich-products/vendor-details/' + vendor.id);
      });
    },
    error =>
      Swal.fire({
        title: 'Error',
        text:
          error?.response?.data?.message ||
          'Error occured while creating distributor, please try again.',
        icon: 'error',
      }),
  );

  const handleNewVendor = (property, value) => {
    setNewVendor(prevVendor => ({
      ...prevVendor,
      [property]: value,
    }));
  };

  const handleSelectChange = (property, options) => {
    setSelectedOptions(options);
    const selectedValuesString = (options || []).map(option => option.value).join(',');
    handleNewVendor(property, selectedValuesString);
  };

  const renderField = key => {
    if (TEXT_FIELDS.includes(key)) {
      return (
        <Form.Control
          className={css(styles.input)}
          defaultValue={null}
          onChange={event => handleNewVendor(key, event.target.value)}
        />
      );
    }
    if (MULTI_SELECT_FIELDS.includes(key)) {
      return (
        <Select
          options={P0SyncAttributeOptions}
          isMulti
          value={selectedOptions}
          onChange={event => handleSelectChange(key, event)}
          styles={selectStyles}
        />
      );
    }
    return (
      <ToggleSwitch
        onColor={grape}
        checked={key in newVendor ? newVendor[key] : false}
        checkedIcon={false}
        uncheckedIcon={false}
        onChange={() => handleNewVendor(key, !newVendor[key])}
      />
    );
  };

  return (
    <Modal show={true} onHide={onHide} size="lg" centered>
      <div className={css(styles.modalContent)}>
        <div className={css(styles.header)}>
          <h2 className={css(styles.title)}>Create New Distributor</h2>
          <Button
            variant="link"
            className={css(styles.closeButton)}
            onClick={onHide}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
        <Modal.Body className={css(styles.body)}>
          {Object.keys(vendorRows).map(key => (
            <Form.Group key={key} className={css(styles.formGroup)}>
              <Form.Label className={css(styles.label)}>{vendorRows[key]}</Form.Label>
              <div>{renderField(key)}</div>
            </Form.Group>
          ))}
        </Modal.Body>
        <div className={css(styles.footer)}>
          <Button
            className={css(styles.primaryButton)}
            disabled={loading}
            onClick={() => createVendor(newVendor)}
          >
            {loading ? (
              <Spinner animation="border" role="status" size="sm" />
            ) : (
              'Create Distributor'
            )}
          </Button>
          <Button className={css(styles.cancelButton)} onClick={() => onHide()}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: `1px solid ${greyStroke}`,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    borderRadius: 6,
    ':hover': {
      backgroundColor: `${grape}15`,
      color: grape,
    },
  },
  body: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    padding: '10px 14px',
    ':focus': {
      borderColor: grape,
      boxShadow: `0 0 0 1px ${grape}`,
    },
  },
  footer: {
    display: 'flex',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${greyStroke}`,
    backgroundColor: white,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      color: coolGrey,
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${grape}`,
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
});

VendorCreateModal.propTypes = {
  onHide: PropTypes.func,
};

export default VendorCreateModal;
